import submitBenefit from "@crescentParts/Handlers/Custom/BenefitHandler";
import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { RetrievePageResult } from "@insite/client-framework/Services/ContentService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCreateAccountReturnUrl } from "@insite/client-framework/Store/Pages/CreateAccount/CreateAccountSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
// eslint-disable-next-line spire/fenced-imports
import { url } from "inspector";
import * as React from "react";
import { useEffect } from "react";
import { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    submitBenefit,
};

const mapStateToProps = (state: ApplicationState) => ({
    accountSettings: getSettingsCollection(state).accountSettings,
    returnUrl: getCreateAccountReturnUrl(state),
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface ProtectedStyles {
    protectedTextField?: TextFieldPresentationProps;
    form?: InjectableCss;
    button?: InjectableCss;
    submitButton?: ButtonPresentationProps;
    loadingWrapper?: InjectableCss;
}

export const protectedStyles: ProtectedStyles = {
    protectedTextField: {
        css: css`
            border-radius: 8px !important;
            width: 3;
        `,
    },

    form: {
        css: css`
            display: flex;
            flex-direction: row;
        `,
    },
    button: {
        css: css`
            align-items: flex-end;
            width: 100%;
            margin-top: 32px;
            margin-left: 10px;
        `,
    },
    loadingWrapper: {
        css: css`
            width: 100vw;
            height: 100vh;
            z-index: 100;
            position: fixed;
            top: 0;
            left: 0;
            background-color: white;
            display: flex;
            justify-content: center;
            align-items: center;
        `,
    },
};

const styles = protectedStyles;
const StyledForm = getStyledWrapper("form");

const PasswordProtected: React.FC<Props> = ({ submitBenefit, history, fields }) => {
    const [showForm, setShowForm] = useState(false);
    const [sessionChecked, setSessionChecked] = useState(false);
    React.useEffect(() => {
        const isBenefitPageAuthenticated = getCookie("isBenefitPageAuthenticated");
        if (isBenefitPageAuthenticated) {
            history.replace("/BenefitsGuide");
        } else {
            setShowForm(true);
            setSessionChecked(true);
        }
    }, []);

    const PasswordRequiredFieldMEssage = siteMessage("Password is Required");

    const [showPassword, setShowPassword] = useState(false);
    const [passwordInput, setPasswordInput] = useState("");
    const [passowrdError, setPasswordError] = useState(PasswordRequiredFieldMEssage);
    const [errorMessage, setErrorMessage] = useState("");
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const toasterContext = React.useContext(ToasterContext);

    const validatePassword = (value: string) => {
        const errorMessage = !value ? PasswordRequiredFieldMEssage : "";
        setPasswordError(errorMessage);
        return !errorMessage;
    };

    const validateSubmitEnabled = () => {
        return !passowrdError;
    };

    const passwordChangeHandler = (value: string) => {
        validatePassword(value);
        setPasswordInput(value);
    };

    const onSuccessHandler = () => {
        setErrorMessage("");
        // toasterContext.addToast({ body: translate("Successfully submitted!"), messageType: "success" });
        const now = new Date();
        const minutes = fields.sessionDuration || 45;
        now.setTime(now.getTime() + minutes * 60 * 1000);
        setCookie("isBenefitPageAuthenticated", "true", {
            expires: now,
        });

        clearFormFields();
        history.push("/BenefitsGuide");
    };

    const clearFormFields = () => {
        setPasswordInput("");
        setErrorMessage("");
        setIsSubmitted(false);
        setIsSubmitting(false);
        setPasswordError(PasswordRequiredFieldMEssage);
    };

    // eslint-disable-next-line require-await
    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!isSubmitted) {
            setIsSubmitted(true);
        }

        if (!validateSubmitEnabled()) {
            return;
        }

        setIsSubmitting(true);

        submitBenefit({
            password: passwordInput,
            onError: error => {
                setErrorMessage(error);
                setIsSubmitting(false);
                const errorMsg = error.toString().length > 400 ? "Something went wrong!" : error;
                toasterContext.addToast({ body: translate(errorMsg), messageType: "danger" });
            },
            onSuccess: onSuccessHandler,
        });
    };

    const isSubmitEnabled = validateSubmitEnabled();

    return (
        <>
            {!sessionChecked && (
                <StyledWrapper {...styles.loadingWrapper}>
                    <LoadingSpinner />
                </StyledWrapper>
            )}

            {sessionChecked && showForm && (
                <StyledForm {...styles.form} onSubmit={submitHandler}>
                    <TextField
                        {...styles.protectedTextField}
                        label="Password"
                        required
                        value={passwordInput}
                        error={isSubmitted && passowrdError}
                        data-test-selector="Benefit_password"
                        onChange={e => passwordChangeHandler(e.currentTarget.value)}
                        iconProps={{
                            src: showPassword ? EyeOff : Eye,
                        }}
                        iconClickableProps={{
                            onClick: () => {
                                setShowPassword(!showPassword);
                            },
                            type: "button",
                        }}
                        clickableText={passwordInput}
                        autoComplete="current-password"
                        type={showPassword ? "text" : "password"}
                    />
                    <StyledWrapper {...styles.button}>
                        <Button
                            {...styles.submitButton}
                            type="submit"
                            disabled={isSubmitting || (isSubmitted && !isSubmitEnabled)}
                        >
                            {translate("Enter")}
                        </Button>
                    </StyledWrapper>
                </StyledForm>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(PasswordProtected)),
    definition: {
        group: "Common",
        displayName: "Password Protected",
        fieldDefinitions: [
            {
                name: "sessionDuration",
                displayName: "Session duration (in min)",
                editorTemplate: "IntegerField",
                defaultValue: 45,
                fieldType: "General",
            },
        ],
    },
};
export default widgetModule;
